import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  changeFreightOrderDetailStatus,
  getFreightOrderView,
  storeFreightOrderOnlyBSBPrice,
  storeFreightOrderProposal
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const statusLabel = (v) => {
  const s = String(v);
  if (s === "1") return "Approved";
  if (s === "2") return "Rejected";
  return "Pending";
};

const FreightOrderViewPage = () => {
  const { id } = useParams();
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Orders", link: "/freight/orders" },
      { label: `Order #${id ?? ""}` }
    ],
    [roleLabel, id]
  );

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [details, setDetails] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [draftPrices, setDraftPrices] = useState({});
  const [proposalUserId, setProposalUserId] = useState("");
  const [proposalBsb, setProposalBsb] = useState("");
  const [proposalPrice, setProposalPrice] = useState("");
  const [busyDetailId, setBusyDetailId] = useState(null);
  const [busyProposal, setBusyProposal] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getFreightOrderView(id);
      const root = res?.data ?? res;
      setOrder(root?.freightOrderData ?? null);
      const d = Array.isArray(root?.freightOrderDetailData) ? root.freightOrderDetailData : [];
      setDetails(d);
      setAvailableUsers(Array.isArray(root?.availableUsers) ? root.availableUsers : []);
      const nextDraft = {};
      for (const row of d) {
        nextDraft[row.id] = {
          bsb_price: row.bsb_price != null ? String(row.bsb_price) : "",
          price: row.price != null ? String(row.price) : ""
        };
      }
      setDraftPrices(nextDraft);
    } catch (e) {
      showError(e.message || "Failed to load order");
      setOrder(null);
      setDetails([]);
      setAvailableUsers([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const orderIdNum = order?.id != null ? Number(order.id) : Number(id);

  const setDraft = (detailId, field, value) => {
    setDraftPrices((prev) => ({
      ...prev,
      [detailId]: { ...prev[detailId], [field]: value }
    }));
  };

  const handleDetailStatus = async (detailId, status) => {
    setBusyDetailId(detailId);
    try {
      await changeFreightOrderDetailStatus(detailId, { status: String(status) });
      showSuccess("Line status updated");
      await load();
    } catch (e) {
      showError(e.message || "Update failed");
    } finally {
      setBusyDetailId(null);
    }
  };

  const handleSaveLinePrices = async (detailId) => {
    const d = draftPrices[detailId];
    if (!d) return;
    setBusyDetailId(detailId);
    try {
      await storeFreightOrderOnlyBSBPrice({
        freight_order_detail_id: detailId,
        bsb_price: String(d.bsb_price ?? "").trim(),
        price: String(d.price ?? "").trim()
      });
      showSuccess("Prices saved");
      await load();
    } catch (e) {
      showError(e.message || "Save failed");
    } finally {
      setBusyDetailId(null);
    }
  };

  const handleProposal = async (e) => {
    e.preventDefault();
    if (!orderIdNum || Number.isNaN(orderIdNum)) {
      showError("Missing order id");
      return;
    }
    const uid = Number(proposalUserId);
    if (!uid) {
      showError("Choose a transporter");
      return;
    }
    setBusyProposal(true);
    try {
      await storeFreightOrderProposal({
        freight_order_id: orderIdNum,
        user_id: uid,
        bsb_price: String(proposalBsb).trim(),
        price: String(proposalPrice).trim()
      });
      showSuccess("Proposal saved");
      setProposalBsb("");
      setProposalPrice("");
      setProposalUserId("");
      await load();
    } catch (err) {
      showError(err.message || "Proposal failed");
    } finally {
      setBusyProposal(false);
    }
  };

  const routeTitle = order
    ? [order.from_area_name, order.from_city_name].filter(Boolean).join(", ") +
      " → " +
      [order.to_area_name, order.to_city_name].filter(Boolean).join(", ")
    : "Freight order";

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px] space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Link to="/freight/orders" className="btn-outline" style={{ textDecoration: "none" }}>
            ← All orders
          </Link>
          {id && (
            <Link
              to={`/freight/orders/${id}/edit`}
              className="btn-primary"
              style={{ textDecoration: "none", display: "inline-block" }}
            >
              Edit order
            </Link>
          )}
        </div>

        {loading && <div className="card offers-table p-6 text-gray-600">Loading…</div>}

        {!loading && !order && (
          <div className="card offers-table p-6 text-gray-700">Order not found.</div>
        )}

        {!loading && order && (
          <>
            <div className="card offers-table border-l-4 border-l-[#064E8D] overflow-hidden">
              <div className="flex flex-wrap justify-between gap-4 items-start p-1">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 m-0 mb-1">Route</p>
                  <h2 className="m-0 text-xl font-semibold text-gray-900">{routeTitle}</h2>
                  <p className="m-0 mt-2 text-sm text-gray-600">
                    <span className="font-medium text-gray-800">Load:</span> {order.load_qty ?? "—"} MT
                    {order.grade_name ? (
                      <>
                        {" "}
                        · <span className="font-medium text-gray-800">Grade:</span> {order.grade_name}
                      </>
                    ) : null}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-gray-700 min-w-[200px]">
                  <div>
                    <span className="text-gray-500">Order ref</span>{" "}
                    <span className="font-mono font-medium">{order.auto_id ?? `#${order.id}`}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card offers-table">
              <h3 className="m-0 mb-3 text-lg">Transporter quotes</h3>
              <div className="freight-admin-table-wrap">
                <table className="freight-admin-table">
                  <thead>
                    <tr>
                      <th>Transporter</th>
                      <th>Code</th>
                      <th>GST</th>
                      <th>BSB price</th>
                      <th>Price</th>
                      <th>RM</th>
                      <th>Approval</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-gray-500">
                          No quotes yet. Add a proposal below.
                        </td>
                      </tr>
                    )}
                    {details.map((row) => {
                      const draft = draftPrices[row.id] || { bsb_price: "", price: "" };
                      const busy = busyDetailId === row.id;
                      return (
                        <tr key={row.id}>
                          <td>{row.user_name ?? "—"}</td>
                          <td className="font-mono text-xs">{row.user_code ?? "—"}</td>
                          <td className="text-xs">{row.user_gst ?? "—"}</td>
                          <td>
                            <input
                              className="w-24 px-2 py-1 border border-gray-200 rounded text-sm"
                              value={draft.bsb_price}
                              onChange={(e) => setDraft(row.id, "bsb_price", e.target.value)}
                              disabled={busy}
                            />
                          </td>
                          <td>
                            <input
                              className="w-24 px-2 py-1 border border-gray-200 rounded text-sm"
                              value={draft.price}
                              onChange={(e) => setDraft(row.id, "price", e.target.value)}
                              disabled={busy}
                            />
                          </td>
                          <td>{row.rm_name ?? "—"}</td>
                          <td>
                            <span
                              className={
                                String(row.is_approve) === "1"
                                  ? "text-green-700 font-medium"
                                  : String(row.is_approve) === "2"
                                    ? "text-red-700 font-medium"
                                    : "text-amber-700 font-medium"
                              }
                            >
                              {statusLabel(row.is_approve)}
                            </span>
                          </td>
                          <td>
                            <div className="freight-admin-actions">
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleSaveLinePrices(row.id)}
                              >
                                Save prices
                              </button>
                              <button type="button" disabled={busy} onClick={() => handleDetailStatus(row.id, "1")}>
                                Approve
                              </button>
                              <button type="button" disabled={busy} onClick={() => handleDetailStatus(row.id, "2")}>
                                Reject
                              </button>
                              <button type="button" disabled={busy} onClick={() => handleDetailStatus(row.id, "0")}>
                                Pending
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card offers-table border border-dashed border-gray-300 bg-gray-50/50">
              <h3 className="m-0 mb-3 text-lg">Add or update proposal</h3>
              <form className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" onSubmit={handleProposal}>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600">Transporter</span>
                  <select
                    className="px-2 py-2 border border-gray-200 rounded bg-white"
                    value={proposalUserId}
                    onChange={(e) => setProposalUserId(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {availableUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                        {String(u.is_verify) === "1" ? " ✓" : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600">BSB price</span>
                  <input
                    className="px-2 py-2 border border-gray-200 rounded bg-white"
                    value={proposalBsb}
                    onChange={(e) => setProposalBsb(e.target.value)}
                    placeholder="e.g. 1250"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600">Price</span>
                  <input
                    className="px-2 py-2 border border-gray-200 rounded bg-white"
                    value={proposalPrice}
                    onChange={(e) => setProposalPrice(e.target.value)}
                    placeholder="e.g. 1200"
                  />
                </label>
                <button type="submit" className="btn-primary h-[42px]" disabled={busyProposal}>
                  {busyProposal ? "Saving…" : "Save proposal"}
                </button>
              </form>
              <p className="text-xs text-gray-500 mt-3 m-0">
                Submitting for an existing transporter updates their row. Use “Save prices” on a line for quick edits.
              </p>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FreightOrderViewPage;
